<script setup>
import { formatTelLink } from '@/composables/useFormatTelLink';
import ApplicationsList from '@/components/ApplicationsList.vue';
import SocialsList from '@/components/SocialsList.vue';
import FormConsultation from '@/components/forms/FormConsultation.vue';

import CIcon from '@/components/common/CIcon.vue';
import { ref } from 'vue';

defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});

const isActive = ref(false);
const isAtFooter = ref(false);

const handleScroll = () => {
  const scrollY = window.scrollY;
  const footer = document.querySelector('footer');

  isActive.value = scrollY > 32;

  if (footer) {
    const footerTop = footer.getBoundingClientRect().top + window.scrollY;
    isAtFooter.value = scrollY + window.innerHeight >= footerTop;
  }
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

const active = ref(false);

const closeContacts = () => {
  active.value = false;
};

const toggleContacts = () => {
  active.value = !active.value;
};
</script>
<template>
  <div :class="['support', active ? 'support--active' : '']">
    <Button
      :class="{ 'at-footer': isAtFooter }"
      class="support__button button-icon-only button-icon-only--dark js-button-support"
      rounded
      variant="text"
      size="large"
      :aria-label="$t('support.open_button')"
      @click.stop="toggleContacts"
    >
      <CIcon
        name="phone-black"
        width="24"
        height="24"
        class="support__button-icon button-icon-only__icon"
      />
    </Button>

    <transition name="morph">
      <div v-show="active" class="support__contacts">
        <ScrollPanel style="max-height: calc(100vh - 56px)">
          <div class="support__contacts-wrap">
            <Button
              text
              size="small"
              class="support__contacts-close js-button-support-close"
              @click="closeContacts"
            >
              <CIcon
                name="close-black"
                width="16"
                height="16"
                class="support__contacts-close-icon"
              />
            </Button>
            <p
              class="mb-0 support__contacts-title h4"
              v-html="data.title"
            ></p>
            <FormConsultation
              class="support__contacts-form"
              :view="'support'"
            />
            <div class="support__contacts-info">
              <div
                v-if="data?.phone && data?.phone.length > 0"
                class="support__contacts-phones"
              >
                <CIcon
                  class="support__contacts-phones-icon"
                  name="phone-red"
                  width="24"
                  height="24"
                />
                <div class="support__contacts-phones-list">
                  <a
                    v-for="phone in data?.phone"
                    :key="phone"
                    :href="`tel:${formatTelLink(phone)}`"
                    class="body-1-bold"
                  >
                    {{ phone }}
                  </a>
                </div>
              </div>
              <SocialsList
                v-if="data?.messengers?.length"
                :data="data.messengers"
                :view="'black'"
                class="support__contacts-messengers"
              />
            </div>

            <ApplicationsList
              v-if="data?.applications?.length"
              :data="data.applications"
              :view="'dark'"
              template="support"
              class="support__contacts-applications"
              :buttons-prefix-class="'js-button-support-application'"
            />
          </div>
        </ScrollPanel>
      </div>
    </transition>
  </div>
</template>
<style scoped lang="scss">
.support {
  $parent: &;
  bottom: 16px;
  max-width: calc(100% - 32px);
  position: fixed;
  right: 16px;
  z-index: 26;
  transition: border-radius 0.3s ease-in-out;
  width: 328px;
  @include media-breakpoint-up(sm) {
    bottom: 24px;
    max-width: unset;
    right: 24px;
    width: 584px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(bottom, 24);
    @include responsive-spacing(right, 24);
    @include responsive-spacing(width, 584);
  }
  &--active {
    #{$parent} {
      &__button {
        opacity: 0;
        z-index: 0;
      }
    }
  }
  &__button {
    background-color: $white;
    box-shadow: 0px 4px 24px 0px #00000029;
    border-radius: 50% !important;
    bottom: 16px;
    position: fixed;
    right: 16px;
    z-index: 100;
    transition: opacity 0.3s linear;
    transition-delay: 1.5s;
    &:active,
    &:hover {
      background-color: $white !important;
    }
    @include media-breakpoint-up(sm) {
      bottom: 24px;
      right: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(bottom, 24);
      @include responsive-spacing(right, 24);
    }
  }
  &__contacts {
    background-color: $white;
    border-radius: 4px;
    box-shadow: 0px 4px 24px 0px #00000029;
    position: absolute;
    right: 0;
    bottom: 0;
    transform-origin: bottom right;
    will-change: transform, opacity, border-radius;
    @include media-breakpoint-up(sm) {
      border-radius: 8px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
    }
    &-wrap {
      align-items: start;
      display: grid;
      gap: 32px;
      padding: 33px 16px 16px;
      grid-template-areas: 'title' 'form' 'info' 'applications';
      position: relative;
      overflow: hidden;
      @include media-breakpoint-up(sm) {
        gap: 40px;
        padding: 40px 0 24px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 40);
        @include responsive-spacing(padding-top, 40);
        @include responsive-spacing(padding-bottom, 24);
      }
    }
    &-close {
      height: 32px;
      position: absolute;
      right: 23px;
      top: 16px;
      width: 32px;
      &-icon {
        height: 100%;
        width: 100%;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(height, 32);
        @include responsive-spacing(right, 23);
        @include responsive-spacing(top, 16);
        @include responsive-spacing(width, 32);
      }
    }
    &-title {
      grid-area: title;
      @include media-breakpoint-up(sm) {
        padding: 0 24px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-left, 24);
        @include responsive-spacing(padding-right, 24);
      }
    }
    &-form {
      grid-area: form;
      @include media-breakpoint-up(sm) {
        display: grid;
        grid-template-columns: auto max-content;
        padding: 0 24px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-left, 24);
        @include responsive-spacing(padding-right, 24);
      }
    }
    &-info {
      display: grid;
      grid-template-columns: 1fr;
      gap: 24px;
      grid-area: info;
      @include media-breakpoint-up(sm) {
        border-top: 1px solid $divider;
        grid-template-columns: auto auto;
        justify-content: space-between;
        padding: 40px 24px 0;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-top-width, 1);
        @include responsive-spacing(gap, 24);
        @include responsive-spacing(padding-left, 24);
        @include responsive-spacing(padding-right, 24);
        @include responsive-spacing(padding-top, 40);
      }
    }
    &-phones {
      align-items: center;
      display: grid;
      gap: 16px;
      grid-template-columns: 24px auto;
      max-width: fit-content;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 16);
        grid-template-columns: px-to-vw(24) auto;
      }
      &-icon {
        height: 100%;
        width: 100%;
      }
      &-list {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 16);
        }
      }
    }
    &-applications {
      grid-area: applications;
      @include media-breakpoint-up(sm) {
        border-top: 1px solid $divider;
        column-gap: 12px;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        padding: 32px 24px 0;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-top-width, 1);
        @include responsive-spacing(column-gap, 12);
        @include responsive-spacing(padding-left, 24);
        @include responsive-spacing(padding-right, 24);
        @include responsive-spacing(padding-top, 32);
      }
    }
  }
}
.morph-enter-active,
.morph-leave-active {
  transition:
    transform 0.5s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.4s ease,
    border-radius 0.5s ease;
}

.morph-enter-from,
.morph-leave-to {
  opacity: 0;
  transform: scale(0);
  border-radius: 50%;
}

.morph-enter-to,
.morph-leave-from {
  opacity: 1;
  transform: scale(1);
}
</style>
